/**
 * ReplacedCopyCommand
 *
 * A placed copy taking the seat of another, as one undo entry: the old copy
 * leaves and the new one arrives, and undo and redo are those two steps the
 * other way round. It is a pair of {@link PlacedCopyCommand}s (the removal, and
 * the placement made with `PlacedCopyCommand.replacing`) that share one root
 * identity, so the wrapper that a swap replaces is the same wrapper to whatever
 * holds it by identity: an interaction target, a Pages path, the
 * reference-identity registry that re-derives them (REFERENCE_IDENTITY.md). The
 * ref is bound to the new root after the swap, to the rebuilt old one after an
 * undo, and to the rebuilt new one after a redo.
 *
 * The pair is one command, not two steps of a sequence, for the recording's
 * sake. A sequence rebuilds its steps one at a time, and the step that places the
 * new copy is rebuilt after the one that removes the old copy has run and its
 * root is gone, with nothing left to tell it which identity it inherits. Here
 * the two are rebuilt together, while the old root still stands, and the
 * placement is handed its ref.
 *
 * It remembers how far it got, as {@link CompositeCommand} does: an undo or a
 * redo that throws part-way is retried from the step that threw.
 */

import type { UndoCommand, SerializedCommand } from './UndoCommand';
import type { SceneObject } from '../../core/SceneObject';
import { PlacedCopyCommand } from './PlacedCopyCommand';
import { deserializeCommand, registerCommandCodec } from './CommandCodec';

const CODEC_TYPE = 'ReplacedCopy';

interface ReplacedCopyPayload {
    label: string;
    removed: SerializedCommand;
    placed: SerializedCommand;
}

export class ReplacedCopyCommand implements UndoCommand {
    readonly label: string;
    private readonly removed: PlacedCopyCommand;
    private readonly placed: PlacedCopyCommand;
    /** The steps applied: 0 the old copy stands, 1 neither does, 2 the new copy stands. */
    private applied: 0 | 1 | 2;

    /**
     * @param removed the removal of the old copy ({@link PlacedCopyCommand.remove})
     * @param placed the placement of the new one, made with
     *   {@link PlacedCopyCommand.replacing} so that its root has the removed
     *   root's identity. Both have already happened.
     */
    constructor(label: string, removed: PlacedCopyCommand, placed: PlacedCopyCommand, applied: 0 | 1 | 2 = 2) {
        this.label = label;
        this.removed = removed;
        this.placed = placed;
        this.applied = applied;
    }

    /** What the new copy holds that is not its own, while it stands: what an undo would delete with it. */
    heldContent(): SceneObject[] {
        return this.applied === 2 ? this.placed.heldContent() : [];
    }

    /**
     * Neither copy stands: the old one is gone and the new one has not arrived
     * (a redo that threw between the two), or the new one is gone and the old
     * one is not back (an undo that did).
     */
    isPartlyApplied(): boolean {
        return this.applied === 1;
    }

    async undo(): Promise<void> {
        if (this.applied === 2) {
            await this.placed.undo();
            this.applied = 1;
        }
        if (this.applied === 1) {
            await this.removed.undo();
            this.applied = 0;
        }
    }

    async redo(): Promise<void> {
        if (this.applied === 0) {
            await this.removed.redo();
            this.applied = 1;
        }
        if (this.applied === 1) {
            await this.placed.redo();
            this.applied = 2;
        }
    }

    serialize(): SerializedCommand {
        const payload: ReplacedCopyPayload = {
            label: this.label,
            removed: this.removed.serialize(),
            placed: this.placed.serialize(),
        };
        return { type: CODEC_TYPE, data: payload };
    }
}

registerCommandCodec(CODEC_TYPE, (data, scene): ReplacedCopyCommand => {
    const p = data as ReplacedCopyPayload;
    // Both are rebuilt now, against the scene as it stands before the pair runs:
    // the removal finds the old root, and the placement names only its parent.
    const removed = deserializeCommand(p.removed, scene);
    const placed = deserializeCommand(p.placed, scene);
    if (!(removed instanceof PlacedCopyCommand) || !(placed instanceof PlacedCopyCommand)) {
        throw new Error(`[ReplacedCopyCommand] "${p.removed.type}" and "${p.placed.type}" are not two placed copies`);
    }
    placed._adoptRootIdentity(removed._rootIdentity());
    return new ReplacedCopyCommand(p.label, removed, placed, 0);
});
